import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Input,
  Pagination,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { Navigate, useParams } from "react-router-dom";
import { useCurrentUser } from "../host/useCurrentUser";
import { LicenseUsageMeter } from "../team/components/LicenseUsageMeter";
import { MemberAvatar } from "../team/components/MemberAvatar";
import { MemberLicenseSelection } from "../team/components/MemberLicenseSelection";
import { PlanBadge } from "../team/components/PlanBadge";
import type { Account, MemberLicense, TeamMember } from "../team/types";
import { useAdminLicenseOverview } from "./useAdminLicenseOverview";
import {
  grantLicense,
  revokeLicense,
  updateMemberRole,
  type AdminSettableRole,
  type LicenseOverviewRow,
} from "./api/licenseOverview";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const ROLE_OPTIONS: { value: AdminSettableRole; label: string }[] = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "editor", label: "Editor" },
  { value: "viewer", label: "Viewer" },
];

// CS-only view: a single team's licence overview, mounted by the admin-app
// (stpancras-admin-app) inside TeamDetail.vue via the zoid bridge. Distinct
// route from the user-facing /team page so URL semantics stay clean:
//   - /team               → the team owner / member view (presenter, embed)
//   - /admin/teams/:id    → CS / staff view of any team's licences
//
// Authorisation is gated server-side on every BE endpoint that this page
// calls; the front-end redirect below is purely UX — a real non-admin who
// lands on this URL gets bounced back to the team view instead of staring
// at an empty page that would 403 every fetch anyway.
export function AdminTeamLicensesPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const currentUser = useCurrentUser();
  // AHA-42877: server-side pagination + email substring search. searchInput
  // is the user's raw typing; debouncedSearch is what we actually send to the
  // backend. Page resets to 1 whenever the debounced term changes (handled
  // in the same effect to avoid stale-page race conditions).
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, loading, error, refetch } = useAdminLicenseOverview(accountId, {
    page,
    size: PAGE_SIZE,
    search: debouncedSearch,
  });
  // Per-user "in-flight" set so each row's dropdown spins independently
  // without freezing the whole table.
  const [pendingUserIds, setPendingUserIds] = useState<Set<string>>(
    () => new Set(),
  );

  const togglePending = useCallback((userId: string, on: boolean) => {
    setPendingUserIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(userId);
      else next.delete(userId);
      return next;
    });
  }, []);

  const onToggleLicense = useCallback(
    async (row: LicenseOverviewRow, next: boolean) => {
      if (!accountId) return;
      const userIdNum = Number(row.user.id);
      if (!Number.isInteger(userIdNum) || userIdNum <= 0) {
        message.error("Cannot toggle licence: invalid user id");
        return;
      }
      const key = String(row.user.id);
      togglePending(key, true);
      try {
        if (next) {
          await grantLicense(accountId, userIdNum);
        } else {
          await revokeLicense(accountId, userIdNum);
        }
        refetch();
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Action failed";
        message.error(msg);
      } finally {
        togglePending(key, false);
      }
    },
    [accountId, refetch, togglePending],
  );

  // AHA-42877: admin-only role mutation. Every role — owner included —
  // applies directly. An earlier revision gated `owner` behind a
  // "Transfer team ownership?" confirm modal, written when the backend
  // routed owner-promotion through transferOwnership (atomic swap that
  // demoted the current owner and moved folder ownership). The backend
  // is now a plain accountId-scoped role update with no side effects, no
  // owner-count invariant, and one-click reversibility via this same
  // dropdown — so the modal's copy was false on every claim and the
  // confirm no longer earns its interruption. (AHA-43231 follow-up.)
  const onChangeRole = useCallback(
    async (row: LicenseOverviewRow, nextRole: AdminSettableRole) => {
      if (!accountId) return;
      const userIdNum = Number(row.user.id);
      if (!Number.isInteger(userIdNum) || userIdNum <= 0) {
        message.error("Cannot change role: invalid user id");
        return;
      }
      const key = String(row.user.id);

      togglePending(key, true);
      try {
        await updateMemberRole(accountId, userIdNum, nextRole);
        refetch();
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Action failed";
        message.error(msg);
      } finally {
        togglePending(key, false);
      }
    },
    [accountId, refetch, togglePending],
  );

  const teamPlan = data?.teamPlan ?? null;
  // AHA-43204: same seat-availability gate as TeamPage so the Grant menu
  // item in the admin grid disables with a tooltip when the team is at
  // cap. CS staff hit the same backend cap as owners (BE enforces in
  // licenses.service), so warning here matches what the server will do.
  // Declared up here (not next to the other derived flags below) because
  // the columns useMemo references it.
  const canGrantMore =
    !!data && data.licenseQuantity - data.numberOfUsedLicenses > 0;

  const columns = useMemo<ColumnsType<LicenseOverviewRow>>(() => {
    return [
      {
        title: "Member",
        key: "user",
        render: (_v, row) => {
          const name = displayName(row.user);
          return (
            // AHA-43204: effective-plan badge sits immediately after the
            // name (same line) instead of in its own column. CS scans
            // dozens of rows looking for "what plan does this member
            // actually get" — locking it to the name keeps the eye in
            // one place rather than tracking across the cell, and the
            // overall column count drops from 4 to 3.
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <MemberAvatar
                id={row.user.id}
                name={name}
                image={row.user.avatar ?? undefined}
              />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 14, lineHeight: "20px" }}>
                    {name}
                  </span>
                  <PlanBadge plan={row.effectivePlan ?? "free"} />
                </div>
                <div style={{ fontSize: 12, color: "#717171", lineHeight: "18px" }}>
                  {row.user.email}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        title: "Personal plan",
        key: "personalPlan",
        render: (_v, row) => (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {/* AHA-43204: mirror the team page's badge treatment — PlanBadge
                renders the keyword-driven palette and prettifies plan keys
                via getPlanDisplayName ("pro-yearly" -> "Pro Yearly", "free"
                -> "Free"). Null-coalesce to "free" so members with no
                personal plan still get a visible label (PlanBadge would
                otherwise render nothing for null). */}
            <PlanBadge plan={row.personalPlan ?? "free"} />
            {isOverriddenByTeamPlan(row) && <OverriddenBadge teamPlan={row.teamPlan} />}
          </div>
        ),
      },
      {
        // Reuses MemberLicenseSelection — same dropdown chrome the
        // user-facing members table uses, so CS sees one consistent
        // affordance. The dropdown reads `member.license` truthiness,
        // so we synthesize a TeamMember-shaped row with a stub
        // MemberLicense object when `hasLicense` is true.
        // The team-plan-as-column is dropped; the per-row team plan
        // is constant across the whole account (it's an Account-level
        // value) so showing it per-row was redundant. The header still
        // surfaces it once for context.
        //
        // Ordered directly after "Personal plan": the two columns answer
        // the same CS question ("what do they pay for themselves" vs
        // "do they hold a team seat") and get compared row-by-row, so
        // they sit adjacent — the Role control goes last.
        title: "Team licence",
        key: "license",
        width: 180,
        render: (_v, row) => {
          const key = String(row.user.id);
          return (
            <MemberLicenseSelection
              member={toTeamMember(row)}
              teamPlan={teamPlan}
              // CS staff can always toggle — the BE re-validates
              // role on every request via `@Auth({ fresh: true })`.
              canEdit
              canGrantMore={canGrantMore}
              pending={pendingUserIds.has(key)}
              onChange={(next) => onToggleLicense(row, next)}
            />
          );
        },
      },
      {
        // AHA-42877: admin-editable role column. Plain antd Select rather
        // than the team-page's MemberRoleSelection — admin context allows
        // *all* roles (including owner), while the team component
        // intentionally restricts based on viewer role. Simpler to use a
        // bare Select here than to bolt admin-mode props onto the team
        // component.
        title: "Role",
        key: "role",
        width: 140,
        render: (_v, row) => {
          const key = String(row.user.id);
          const current = (row.user.accountRole as AdminSettableRole) ?? "editor";
          return (
            <Select<AdminSettableRole>
              value={current}
              options={ROLE_OPTIONS}
              onChange={(next) => onChangeRole(row, next)}
              disabled={pendingUserIds.has(key)}
              style={{ width: "100%" }}
              data-testid="admin-team-member-role-select"
            />
          );
        },
      },
    ];
  }, [onToggleLicense, onChangeRole, pendingUserIds, teamPlan, canGrantMore]);

  // Until the zoid handshake completes, currentUser is null — render
  // nothing rather than redirecting (the redirect would race the
  // handshake and bounce real admins out).
  if (!currentUser) return null;

  const isStaff = currentUser.role === "admin" || currentUser.role === "master";
  if (!isStaff) {
    return <Navigate to="/team" replace />;
  }

  const rows = data?.rows ?? [];

  // Synthesised Account shape for LicenseUsageMeter — that component
  // only reads `id`, `numberOfUsedLicenses`, and `licenseQuantity` from
  // the account, so a minimal stub is enough. `isPaidTeamPlan` is
  // derived inline (any non-`free`, non-null team plan counts).
  const meterAccount: Account | null = data
    ? {
        id: data.accountId,
        name: data.accountName ?? "",
        numberOfUsedLicenses: data.numberOfUsedLicenses,
        licenseQuantity: data.licenseQuantity,
      }
    : null;
  const isPaidTeamPlan = teamPlan != null && teamPlan !== "free";
  const isUsedLicenseGreaterThanQuota =
    !!data && data.numberOfUsedLicenses > data.licenseQuantity;

  return (
    <div style={{ padding: 24 }}>
      <header
        style={{
          marginBottom: 16,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Team licences — {data?.accountName ?? `account ${accountId}`}
          </Typography.Title>
          {/* AHA-43204: use the same PlanBadge component the table rows
              use so the header reads the team plan in the same visual
              language as the per-row effective-plan badge. The label and
              count remain as Typography.Text alongside it. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 4,
              flexWrap: "wrap",
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              Team plan:
            </Typography.Text>
            <PlanBadge plan={teamPlan ?? "free"} />
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {/* AHA-42877: count comes from server-side totalUsers now —
                  rows.length only reflects the current page. Filtered count
                  when a search is active. */}
              · {data?.totalUsers ?? 0} member
              {(data?.totalUsers ?? 0) === 1 ? "" : "s"}
              {debouncedSearch ? " matching" : ""}
            </Typography.Text>
          </div>
        </div>
        {/* CS always-visible — passing `currentUserIsTeamOwnerOrAdmin: true`
            because CS is the one operating this view. The meter still
            hides itself when there's no paid plan or quota is zero. */}
        <LicenseUsageMeter
          account={meterAccount}
          currentUserIsTeamOwnerOrAdmin
          isPaidTeamPlan={isPaidTeamPlan}
          isUsedLicenseGreaterThanQuota={isUsedLicenseGreaterThanQuota}
        />
      </header>

      {error && (
        <Alert
          type="error"
          message="Failed to load licence overview"
          description={error}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* AHA-42877: search input — debounced to one request per 300ms of
          typing. Email-only substring match (firstName/lastName aren't
          indexed and the SCIM-scale teams can have 100k members, so we
          intentionally don't fan the search across name columns). */}
      <div style={{ marginBottom: 12, maxWidth: 360 }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search by email"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          data-testid="admin-team-search-input"
        />
      </div>

      <Spin spinning={loading}>
        <Table<LicenseOverviewRow>
          rowKey={(r) => String(r.user.id || r.user.email)}
          columns={columns}
          dataSource={rows}
          // AHA-42877: server-side pagination — antd's built-in pagination
          // would slice the already-paginated dataSource, which is wrong.
          // Render an explicit Pagination below the table instead.
          pagination={false}
        />
      </Spin>
      {(data?.totalUsers ?? 0) > PAGE_SIZE && (
        <div
          style={{
            marginTop: 16,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Pagination
            current={page}
            total={data?.totalUsers ?? 0}
            pageSize={PAGE_SIZE}
            showSizeChanger={false}
            onChange={setPage}
          />
        </div>
      )}
    </div>
  );
}

// Adapter — MemberLicenseSelection expects the full TeamMember shape but
// only reads `member.license` truthiness internally. We give it just
// enough surface to render correctly.
function toTeamMember(row: LicenseOverviewRow): TeamMember {
  const license: MemberLicense | null = row.hasLicense
    ? {
        // Synthetic id — MemberLicenseSelection never reads it. Kept
        // distinct from any real license id so a future bug that
        // surfaces it can't accidentally key on this stub.
        id: `stub:${row.user.id}`,
        grantedAt: new Date(),
        grantedSource: "admin",
        grantedByUserId: null,
      }
    : null;
  return {
    id: row.user.id,
    email: row.user.email,
    firstName: row.user.firstName ?? undefined,
    lastName: row.user.lastName ?? undefined,
    avatar: row.user.avatar ?? undefined,
    accountRole: row.user.accountRole ?? undefined,
    license,
  };
}

// Show the "Overridden by team plan" badge only when the member is
// licensed AND their personal plan is free / missing AND there's a
// real (non-free) team plan to override with. Three guards because
// the effective-plan rule is "personal wins if paid, otherwise team"
// — a free→free swap isn't a meaningful override, and an unlicensed
// member doesn't pick up the team plan at all.
function isOverriddenByTeamPlan(row: LicenseOverviewRow): boolean {
  if (!row.hasLicense) return false;
  const personalIsPaid =
    row.personalPlan != null && row.personalPlan !== "free";
  if (personalIsPaid) return false;
  return row.teamPlan != null && row.teamPlan !== "free";
}

function OverriddenBadge({ teamPlan }: { teamPlan: string | null }) {
  return (
    <Tooltip
      title={
        <span>
          This member doesn't have a personal paid plan, but they hold a
          team licence, so their effective plan is the team's
          {teamPlan ? ` (${teamPlan})` : ""}.
        </span>
      }
    >
      <Tag color="orange" style={{ marginInlineEnd: 0, cursor: "help" }}>
        Overridden by team plan
      </Tag>
    </Tooltip>
  );
}

function displayName(user: LicenseOverviewRow["user"]): string {
  const full = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return full || user.email || `User ${user.id}`;
}
